import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useDocumentTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useTemplateVariables,
  usePreviewTemplate,
  type TemplateCategory,
  type PageSize,
  type Orientation,
  type CreateTemplateInput,
} from './hooks/useDocumentTemplates';
import { usePracticeEntities } from '@/features/practice-entities';
import { VariableInserter } from './components/VariableInserter';
import { TemplatePreview } from './components/TemplatePreview';
import { StarterTemplates, type StarterTemplate } from './components/StarterTemplates';
import { EditorToolbar } from './components/EditorToolbar';
import { VisualEditor } from './components/VisualEditor';
import { Dialog } from '@/components/ui/Dialog';
import {
  REPORT_DATA_SOURCES,
  reportVariablesForSource,
  staticColumnsForSource,
  type ReportDataSource,
} from './lib/reportPlaceholders';
import {
  useSavedBuilderReports,
  useReportBuilderPreview,
} from '@/features/reports/hooks/useReportBuilder';

const CATEGORIES: { value: TemplateCategory; label: string }[] = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'debit_note', label: 'Debit Note' },
  { value: 'quote', label: 'Quote' },
  { value: 'statement', label: 'Statement' },
  { value: 'report', label: 'Report' },
  { value: 'letter', label: 'Letter' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'write_off', label: 'Write-Off' },
  { value: 'advance', label: 'Advance' },
];

/**
 * Blank-template content. Kept in sync with the "Blank Template" starter in
 * StarterTemplates.tsx. When a user switches category, the template is reset to
 * this blank state because placeholders/variables differ per category.
 */
const BLANK_BODY_HTML = `<div style="padding:20px;">\n  <h1>{{firm.name}}</h1>\n  <p>Document generated on {{formatDate generated_date}}</p>\n  <!-- Start building your template here -->\n</div>`;

/**
 * Starter body for a report template. Renders the report title/header only. The
 * data table is added by the user via the "Report Columns" block, so only the
 * columns they choose are printed. (We intentionally do NOT include an
 * all-columns table here — otherwise it would print alongside the user's
 * curated table.)
 */
const REPORT_BODY_HTML = `<div style="padding:20px;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #4f46e5;padding-bottom:12px;margin-bottom:16px;">
    <div>
      <h1 style="margin:0;font-size:22px;color:#1f2937;">{{report.title}}</h1>
      <p style="margin:4px 0 0;font-size:11px;color:#6b7280;">{{firm.name}}</p>
    </div>
    <div style="text-align:right;font-size:11px;color:#6b7280;">
      <p style="margin:0;">Generated: {{formatDate report.generated_at}}</p>
      <p style="margin:2px 0 0;">Total rows: {{report.total_rows}}</p>
    </div>
  </div>

  <!-- Use the "Report Columns" block (left menu) to insert a table of the columns you want to print. -->
</div>`;

/**
 * Removes the legacy "all columns" auto-table from a report template body.
 *
 * Early report templates embedded a table that looped over every report column
 * (`{{#each ../columns}}`). That printed all columns alongside any curated table
 * the user later inserted. This strips such tables so only the user's chosen
 * columns remain. Matches a <table>…</table> that contains `{{#each ../columns}}`.
 */
function stripLegacyAllColumnsTable(html: string): string {
  if (!html || !html.includes('../columns')) return html;
  return html.replace(/<table[\s\S]*?<\/table>/gi, (match) =>
    match.includes('../columns') ? '' : match,
  );
}

export function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new';
  // Optional template-type hint from the listing page ('billing' | 'report').
  const typeHint = searchParams.get('type');

  // Step state for new templates. Report templates take a different path than
  // billing/document templates (pick a report data source + report first).
  // When the listing already told us the type, skip the in-editor type picker.
  const initialStep = !isNew
    ? 'editor'
    : typeHint === 'report'
      ? 'editor'
      : typeHint === 'billing'
        ? 'pick-starter'
        : 'pick-type';
  const [step, setStep] = useState<'pick-type' | 'pick-starter' | 'pick-report' | 'editor'>(
    initialStep,
  );
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');

  // Report-template selection state
  const [reportDataSource, setReportDataSource] = useState<ReportDataSource | ''>('');
  const [reportSavedReportId, setReportSavedReportId] = useState<string>('');
  // The columns the selected report produces (for the column-insert block).
  const [reportColumns, setReportColumns] = useState<{ id: string; label: string }[]>([]);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TemplateCategory>(
    typeHint === 'report' ? 'report' : 'invoice',
  );
  // Category the user is attempting to switch to, pending confirmation because
  // switching clears the template (placeholders/variables differ per category).
  const [pendingCategory, setPendingCategory] = useState<TemplateCategory | null>(null);
  // Pending report selection changes (data source / report). Switching either
  // clears the template because the available placeholders change.
  const [pendingReportChange, setPendingReportChange] = useState<
    { kind: 'source'; value: ReportDataSource | '' } | { kind: 'report'; value: string } | null
  >(null);
  const [practiceEntityId, setPracticeEntityId] = useState<string>('');
  const [pageSize, setPageSize] = useState<PageSize>('A4');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [headerHtml, setHeaderHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState(isNew && typeHint === 'report' ? REPORT_BODY_HTML : '');
  const [footerHtml, setFooterHtml] = useState('');
  const [css, setCss] = useState('');
  const [sampleData, setSampleData] = useState('{}');
  const [isDefault, setIsDefault] = useState(false);
  const [activeTab, setActiveTab] = useState<'body' | 'header' | 'footer' | 'css' | 'settings'>(
    'body',
  );
  const [showPreview, setShowPreview] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const headerRef = useRef<HTMLTextAreaElement>(null);
  const footerRef = useRef<HTMLTextAreaElement>(null);

  // Queries & mutations
  const { data: template, isLoading } = useDocumentTemplate(isNew ? undefined : id);
  const { data: variables } = useTemplateVariables(category);
  const { data: practiceEntities = [] } = usePracticeEntities({ isActive: true });
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const previewMutation = usePreviewTemplate();
  const { data: savedBuilderReports = [] } = useSavedBuilderReports();
  const reportColumnsPreview = useReportBuilderPreview();

  const isReport = category === 'report';

  // Populate form from loaded template
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setCategory(template.category);
      setPracticeEntityId(template.practice_entity_id || '');
      setPageSize(template.page_size);
      setOrientation(template.orientation);
      setHeaderHtml(template.header_html || '');
      // Clean up the legacy all-columns table so existing report templates
      // print only the user's chosen columns after a re-save.
      setBodyHtml(
        template.category === 'report'
          ? stripLegacyAllColumnsTable(template.body_html)
          : template.body_html,
      );
      setFooterHtml(template.footer_html || '');
      setCss(template.css || '');
      setSampleData(template.sample_data ? JSON.stringify(template.sample_data, null, 2) : '{}');
      setIsDefault(template.is_default);
      setReportDataSource((template.report_category as ReportDataSource) || '');
      setReportSavedReportId(template.report_saved_report_id || '');
    }
  }, [template]);

  // Report templates derive placeholders from the selected data source, not from
  // the server's per-category variable endpoint.
  const reportVariables = useMemo(
    () => reportVariablesForSource(reportDataSource || null),
    [reportDataSource],
  );
  const effectiveVariables = isReport ? reportVariables : variables;

  // Saved reports available under the currently selected report data source.
  const reportsForSource = useMemo(
    () =>
      savedBuilderReports.filter((r) => !reportDataSource || r.query?.source === reportDataSource),
    [savedBuilderReports, reportDataSource],
  );

  // Discover the selected report's actual columns (by running its query), with
  // a static fallback. These feed the "Report Columns" insert block. Runs once
  // per selected report id.
  const lastDiscoveredReportId = useRef<string>('');
  useEffect(() => {
    if (!isReport) return;
    if (!reportSavedReportId) {
      setReportColumns([]);
      lastDiscoveredReportId.current = '';
      return;
    }
    if (lastDiscoveredReportId.current === reportSavedReportId) return;
    lastDiscoveredReportId.current = reportSavedReportId;

    const saved = savedBuilderReports.find((r) => r.id === reportSavedReportId);
    const fallback = staticColumnsForSource(reportDataSource || null);
    setReportColumns(fallback);

    if (saved?.query?.source) {
      reportColumnsPreview
        .mutateAsync(saved.query)
        .then((result) => {
          if (result.columns && result.columns.length > 0) {
            setReportColumns(result.columns.map((c) => ({ id: c.id, label: c.label })));
          }
        })
        .catch(() => {
          /* keep the static fallback */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReport, reportSavedReportId, reportDataSource]);

  // Handle starter template selection
  const handleStarterSelect = (starter: StarterTemplate) => {
    setCategory(starter.category);
    setHeaderHtml(starter.header_html);
    setBodyHtml(starter.body_html);
    setFooterHtml(starter.footer_html);
    setCss(starter.css);
    setSampleData(JSON.stringify(starter.sample_data, null, 2));
    setName(starter.name === 'Blank Template' ? '' : `My ${starter.name}`);
    setDescription(starter.description);
    setStep('editor');
  };

  /** Chooses a template type from the initial picker. */
  const handleSelectType = (type: 'billing' | 'report') => {
    if (type === 'report') {
      // Report category + report are chosen on the editor screen itself.
      setCategory('report');
      setHeaderHtml('');
      setBodyHtml(REPORT_BODY_HTML);
      setFooterHtml('');
      setCss('');
      setSampleData('{}');
      setStep('editor');
    } else {
      setCategory('invoice');
      setStep('pick-starter');
    }
  };

  /**
   * Returns true when the template has meaningful content that would be lost by
   * resetting to a blank template. An untouched blank template (empty or just
   * the default blank body) is not considered dirty.
   */
  const hasContentToLose = (): boolean => {
    const normalize = (v: string) => v.trim();
    const bodyIsBlank =
      normalize(bodyHtml) === '' || normalize(bodyHtml) === normalize(BLANK_BODY_HTML);
    const sampleIsBlank = normalize(sampleData) === '' || normalize(sampleData) === '{}';
    return (
      !bodyIsBlank ||
      normalize(headerHtml) !== '' ||
      normalize(footerHtml) !== '' ||
      normalize(css) !== '' ||
      !sampleIsBlank
    );
  };

  /** Requests a category change, prompting for confirmation if content exists. */
  const requestCategoryChange = (next: TemplateCategory) => {
    if (next === category) return;
    if (hasContentToLose()) {
      setPendingCategory(next);
    } else {
      setCategory(next);
    }
  };

  /** Applies the pending category change, resetting the template to blank. */
  const confirmCategoryChange = () => {
    if (!pendingCategory) return;
    setCategory(pendingCategory);
    setHeaderHtml('');
    setBodyHtml(BLANK_BODY_HTML);
    setFooterHtml('');
    setCss('');
    setSampleData('{}');
    setPendingCategory(null);
  };

  /** Resets a report template to its starter state (used after a selection change). */
  const resetReportTemplateBody = () => {
    setHeaderHtml('');
    setBodyHtml(REPORT_BODY_HTML);
    setFooterHtml('');
    setCss('');
    setSampleData('{}');
  };

  /** Requests a report data-source change, confirming if content would be lost. */
  const requestReportSourceChange = (next: ReportDataSource | '') => {
    if (next === reportDataSource) return;
    if (hasContentToLose()) {
      setPendingReportChange({ kind: 'source', value: next });
    } else {
      setReportDataSource(next);
      setReportSavedReportId('');
    }
  };

  /** Requests a report change, confirming if content would be lost. */
  const requestReportChange = (next: string) => {
    if (next === reportSavedReportId) return;
    if (hasContentToLose()) {
      setPendingReportChange({ kind: 'report', value: next });
    } else {
      setReportSavedReportId(next);
    }
  };

  /** Applies the pending report selection change and resets the template. */
  const confirmReportChange = () => {
    if (!pendingReportChange) return;
    if (pendingReportChange.kind === 'source') {
      setReportDataSource(pendingReportChange.value);
      setReportSavedReportId('');
    } else {
      setReportSavedReportId(pendingReportChange.value);
    }
    resetReportTemplateBody();
    setPendingReportChange(null);
  };

  const handleSave = () => {
    performSave();
  };

  const performSave = () => {
    let parsedSampleData: Record<string, unknown> | null = null;
    try {
      parsedSampleData = JSON.parse(sampleData);
    } catch {
      /* ignore */
    }

    const input: CreateTemplateInput = {
      name,
      description: description || null,
      category,
      practice_entity_id: isReport ? null : practiceEntityId,
      page_size: pageSize,
      orientation,
      header_html: headerHtml || null,
      body_html: bodyHtml || '<div></div>',
      footer_html: footerHtml || null,
      css: css || null,
      sample_data: parsedSampleData,
      is_default: isDefault,
      report_category: isReport ? reportDataSource || null : null,
      report_saved_report_id: isReport ? reportSavedReportId || null : null,
    };

    if (isNew) {
      createMutation.mutate(input, {
        onSuccess: (data) => navigate(`/settings/document-templates/${data.id}`),
      });
    } else {
      updateMutation.mutate({ id: id!, ...input });
    }
  };

  const handlePreview = () => {
    if (!id || isNew) {
      // For new templates, show inline preview using current body
      setShowPreview(true);
      return;
    }
    let data = {};
    try {
      data = JSON.parse(sampleData);
    } catch {
      /* use empty */
    }
    previewMutation.mutate({ id, data });
    setShowPreview(true);
  };

  /** Inserts text at the cursor position of the active textarea */
  const insertAtCursor = (text: string) => {
    const ref = activeTab === 'header' ? headerRef : activeTab === 'footer' ? footerRef : bodyRef;
    const textarea = ref.current;
    if (!textarea) {
      // Fallback: append
      if (activeTab === 'body') setBodyHtml((prev) => prev + '\n' + text);
      else if (activeTab === 'header') setHeaderHtml((prev) => prev + '\n' + text);
      else if (activeTab === 'footer') setFooterHtml((prev) => prev + '\n' + text);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);

    if (activeTab === 'body') setBodyHtml(newValue);
    else if (activeTab === 'header') setHeaderHtml(newValue);
    else if (activeTab === 'footer') setFooterHtml(newValue);

    // Restore cursor position after insertion
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    });
  };

  const handleInsertVariable = (variableKey: string) => {
    // Array-item variables (e.g. `line_items[].description`) are not valid
    // Handlebars on their own — they must sit inside a loop over the array.
    // Insert the loop so the value actually prints, formatted by its type.
    const arrayItem = /^([A-Za-z_]\w*)\[\]\.([\w.]+)$/.exec(variableKey);
    if (arrayItem) {
      const [, arrayName, field] = arrayItem;
      const type = (effectiveVariables || []).find((v) => v.key === variableKey)?.type;
      const expr =
        type === 'currency'
          ? `{{currency this.${field}}}`
          : type === 'date'
            ? `{{formatDate this.${field}}}`
            : `{{this.${field}}}`;
      insertAtCursor(`{{#each ${arrayName}}}<div>${expr}</div>{{/each}}`);
      return;
    }
    insertAtCursor(`{{${variableKey}}}`);
  };

  const handleInsertBlock = (html: string) => {
    insertAtCursor('\n' + html + '\n');
  };

  // Loading state for existing templates
  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  // Step 0: Template type picker (new templates only) — Billing vs Report
  if (step === 'pick-type') {
    return (
      <div className="mx-auto max-w-4xl py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings/document-templates')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            Back to Templates
          </button>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          What kind of template do you want to create?
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Choose a template type. Each type exposes different placeholders and data.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <button
            onClick={() => handleSelectType('billing')}
            className="flex flex-col items-start rounded-lg border border-gray-200 p-5 text-left transition hover:border-indigo-500 hover:shadow-sm dark:border-gray-700 dark:hover:border-indigo-400"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z"
                />
              </svg>
            </span>
            <span className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              Billing / Document Template
            </span>
            <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Invoices, quotes, credit notes, statements, receipts, letters, and more.
            </span>
          </button>

          <button
            onClick={() => handleSelectType('report')}
            className="flex flex-col items-start rounded-lg border border-gray-200 p-5 text-left transition hover:border-indigo-500 hover:shadow-sm dark:border-gray-700 dark:hover:border-indigo-400"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h12M3.75 3h-.75m.75 0h16.5m0 0h.75m-.75 0v11.25A2.25 2.25 0 0 1 18 16.5m0 0V21m-6-5.25V21M9 12l2.25 2.25L15 9"
                />
              </svg>
            </span>
            <span className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              Report Template
            </span>
            <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Design a printable layout for a saved report, using its data fields.
            </span>
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Starter template picker (new templates only)
  if (step === 'pick-starter') {
    return (
      <div className="mx-auto max-w-4xl py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings/document-templates')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            Back to Templates
          </button>
        </div>
        <StarterTemplates onSelect={handleStarterSelect} onSkip={() => setStep('editor')} />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Step 2: Editor
  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/settings/document-templates')}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Back to templates"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isNew ? 'New Template' : `Edit: ${template?.name || ''}`}
          </h1>
          {!isNew && template && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              v{template.version}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePreview}
            disabled={previewMutation.isPending}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={
              isSaving ||
              !name.trim() ||
              !bodyHtml.trim() ||
              (isReport ? !reportDataSource || !reportSavedReportId : !practiceEntityId)
            }
            className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isNew ? 'Create Template' : 'Save'}
          </button>
        </div>
      </div>

      {/* Template Metadata */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label
              htmlFor="template-name"
              className="block text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Name
            </label>
            <input
              id="template-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Standard Invoice"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
          {isReport ? (
            <>
              <div>
                <label
                  htmlFor="report-data-source"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-400"
                >
                  Report Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="report-data-source"
                  value={reportDataSource}
                  onChange={(e) =>
                    requestReportSourceChange(e.target.value as ReportDataSource | '')
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">Select category…</option>
                  {REPORT_DATA_SOURCES.map((src) => (
                    <option key={src.value} value={src.value}>
                      {src.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="report-saved-report"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-400"
                >
                  Report <span className="text-red-500">*</span>
                </label>
                <select
                  id="report-saved-report"
                  value={reportSavedReportId}
                  onChange={(e) => requestReportChange(e.target.value)}
                  disabled={!reportDataSource}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">
                    {reportDataSource ? 'Select report…' : 'Select a category first'}
                  </option>
                  {reportsForSource.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label
                  htmlFor="template-category"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-400"
                >
                  Category
                </label>
                <select
                  id="template-category"
                  value={category}
                  onChange={(e) => requestCategoryChange(e.target.value as TemplateCategory)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  {CATEGORIES.filter((cat) => cat.value !== 'report').map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="template-entity"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-400"
                >
                  Practice Entity <span className="text-red-500">*</span>
                </label>
                <select
                  id="template-entity"
                  value={practiceEntityId}
                  onChange={(e) => setPracticeEntityId(e.target.value)}
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="" disabled>
                    Select Entity
                  </option>
                  {practiceEntities.map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
        <div className="mt-3 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              id="is-default"
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
            <label htmlFor="is-default" className="text-xs text-gray-600 dark:text-gray-400">
              Default
            </label>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">Page:</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value as PageSize)}
              className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              aria-label="Page size"
            >
              <option value="A4">A4</option>
              <option value="A3">A3</option>
              <option value="Letter">Letter</option>
              <option value="Legal">Legal</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">Orientation:</label>
            <select
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as Orientation)}
              className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              aria-label="Orientation"
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex gap-4">
        {/* Editor Panel */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Mode Toggle + Tab Navigation */}
          <div className="flex items-center justify-between">
            {editorMode === 'code' ? (
              <nav
                className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800"
                aria-label="Template sections"
              >
                {(['body', 'header', 'footer', 'css', 'settings'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? 'bg-white text-indigo-700 shadow-sm dark:bg-gray-700 dark:text-indigo-400'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            ) : (
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Drag and drop to design your template
              </div>
            )}

            {/* Mode Toggle */}
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
              <button
                onClick={() => setEditorMode('visual')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  editorMode === 'visual'
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                }`}
              >
                Visual
              </button>
              <button
                onClick={() => setEditorMode('code')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  editorMode === 'code'
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                }`}
              >
                Code
              </button>
            </div>
          </div>

          {/* Visual Editor Mode */}
          {editorMode === 'visual' && (
            <VisualEditor
              category={category}
              headerHtml={headerHtml}
              bodyHtml={bodyHtml}
              footerHtml={footerHtml}
              onHeaderChange={setHeaderHtml}
              onBodyChange={setBodyHtml}
              onFooterChange={setFooterHtml}
              pageSize={pageSize}
              orientation={orientation}
              variables={effectiveVariables || []}
              reportColumns={isReport ? reportColumns : undefined}
              reportColumnsLoading={isReport && reportColumnsPreview.isPending}
            />
          )}

          {/* Code Editor Mode */}
          {editorMode === 'code' && (
            <>
              {/* Toolbar (for HTML tabs only) */}
              {(activeTab === 'body' || activeTab === 'header' || activeTab === 'footer') && (
                <EditorToolbar category={category} onInsertBlock={handleInsertBlock} />
              )}

              {/* Tab Content */}
              {activeTab === 'body' && (
                <textarea
                  ref={bodyRef}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={22}
                  placeholder="Enter your template body HTML here..."
                  className="w-full rounded-md border border-gray-300 p-3 font-mono text-xs leading-relaxed focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  spellCheck={false}
                />
              )}

              {activeTab === 'header' && (
                <textarea
                  ref={headerRef}
                  value={headerHtml}
                  onChange={(e) => setHeaderHtml(e.target.value)}
                  rows={14}
                  placeholder="Header HTML — appears at the top of the document (firm logo, title, invoice number...)"
                  className="w-full rounded-md border border-gray-300 p-3 font-mono text-xs leading-relaxed focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  spellCheck={false}
                />
              )}

              {activeTab === 'footer' && (
                <textarea
                  ref={footerRef}
                  value={footerHtml}
                  onChange={(e) => setFooterHtml(e.target.value)}
                  rows={8}
                  placeholder="Footer HTML — appears at the bottom (payment terms, contact info, page numbers...)"
                  className="w-full rounded-md border border-gray-300 p-3 font-mono text-xs leading-relaxed focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  spellCheck={false}
                />
              )}

              {activeTab === 'css' && (
                <textarea
                  value={css}
                  onChange={(e) => setCss(e.target.value)}
                  rows={16}
                  placeholder="Custom CSS styles applied to the document..."
                  className="w-full rounded-md border border-gray-300 p-3 font-mono text-xs leading-relaxed focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  spellCheck={false}
                />
              )}

              {activeTab === 'settings' && (
                <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                  <div>
                    <label
                      htmlFor="template-description"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Description
                    </label>
                    <textarea
                      id="template-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      placeholder="Brief description of this template's purpose..."
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="sample-data"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Sample Data{' '}
                      <span className="font-normal text-gray-400">
                        (JSON — used for preview rendering)
                      </span>
                    </label>
                    <textarea
                      id="sample-data"
                      value={sampleData}
                      onChange={(e) => setSampleData(e.target.value)}
                      rows={12}
                      className="mt-1 w-full rounded-md border border-gray-300 p-3 font-mono text-xs dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      spellCheck={false}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar: Variables (code mode) or nothing (visual mode has built-in panels) */}
        {editorMode === 'code' && activeTab !== 'settings' && activeTab !== 'css' && (
          <VariableInserter variables={effectiveVariables || []} onInsert={handleInsertVariable} />
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <TemplatePreview
          html={previewMutation.data?.html || ''}
          isLoading={previewMutation.isPending}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Confirm losing content when switching category */}
      <Dialog
        open={pendingCategory !== null}
        onClose={() => setPendingCategory(null)}
        onConfirm={confirmCategoryChange}
        title="Change category?"
        description={
          pendingCategory
            ? `Switching to "${CATEGORIES.find((c) => c.value === pendingCategory)?.label ?? pendingCategory}" will clear this template. Each category uses different placeholders and variables, so the template will be reset to blank and your current changes will be lost.`
            : ''
        }
        confirmLabel="Change & clear"
        cancelLabel="Keep current"
        confirmVariant="danger"
      />

      {/* Confirm losing content when changing the report source or report */}
      <Dialog
        open={pendingReportChange !== null}
        onClose={() => setPendingReportChange(null)}
        onConfirm={confirmReportChange}
        title={
          pendingReportChange?.kind === 'source' ? 'Change report category?' : 'Change report?'
        }
        description="Changing the report selection will reset this template. The available data fields depend on the selected report, so the template will be reset to its starter layout and your current changes will be lost."
        confirmLabel="Change & clear"
        cancelLabel="Keep current"
        confirmVariant="danger"
      />
    </div>
  );
}
